import {
  BookOutlined,
  CreditCardOutlined,
  GiftOutlined,
  GlobalOutlined,
  LinkOutlined,
  MessageOutlined,
  ReadOutlined,
  ShareAltOutlined,
  StarOutlined,
  TeamOutlined,
  TelegramFilled,
  TrophyOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";
import type { PortalNavBuiltInKey, PortalNavIcon } from "@axl/membership-sdk";
import { ReactNode, useMemo } from "react";
import { useLocation } from "react-router-dom";

import { usePortalDesign } from "@app/shared/lib/design/portal-design";
import { useLoc, useSchool } from "@app/shared/lib/hooks";

export type NavItem = {
  key: string;
  path: string;
  label: string;
  icon: ReactNode;
  /** A shorter label for the mobile bottom bar, where ~90px fit (default: `label`). */
  tabLabel?: string;
  /** Shown in the mobile bottom bar (the rest go to the "Profile" sheet). */
  primary: boolean;
  /** A school's own external link (portal design `nav.items`, key `custom`): opens in a new tab. */
  external?: boolean;
};

const CUSTOM_ICONS: Record<PortalNavIcon, ReactNode> = {
  link: <LinkOutlined />,
  globe: <GlobalOutlined />,
  telegram: <TelegramFilled />,
  youtube: <YoutubeOutlined />,
  book: <BookOutlined />,
  chat: <MessageOutlined />,
  star: <StarOutlined />,
  gift: <GiftOutlined />,
};

/**
 * The school's top-level sections, in menu order; disabled modules are left out. Portal design `nav.items`
 * re-orders, hides (the page stays reachable by URL) and renames the built-ins and adds external links;
 * built-ins the list leaves out keep their default order after the listed ones.
 */
export function useNavItems() {
  const loc = useLoc();
  const school = useSchool();
  const { nav } = usePortalDesign();
  const { pathname } = useLocation();

  const items = useMemo<NavItem[]>(() => {
    const builtIns: Record<PortalNavBuiltInKey, NavItem | false | null | undefined> = {
      learning: {
        key: "learning",
        path: "/",
        label: loc.word("School.nav.learning", { default: "Learning" }),
        icon: <ReadOutlined />,
        primary: true,
      },
      community: school.community?.enabled && {
        key: "community",
        path: "/community",
        label: loc.word("Community"),
        icon: <TeamOutlined />,
        primary: true,
      },
      achievements: !!school.gamification && {
        key: "gamification",
        path: "/gamification",
        label: loc.word("School.nav.achievements", { default: "Achievements" }),
        icon: <TrophyOutlined />,
        primary: false,
      },
      access: {
        key: "access",
        path: "/access",
        label: loc.word("Access and payments"),
        tabLabel: loc.word("School.nav.accessShort", { default: "Access" }),
        icon: <CreditCardOutlined />,
        primary: true,
      },
      partnership: !!school.partnership && {
        key: "partnership",
        path: "/partnership",
        label: loc.word("Partnership"),
        icon: <ShareAltOutlined />,
        primary: false,
      },
    };
    const order: PortalNavBuiltInKey[] = ["learning", "community", "achievements", "access", "partnership"];
    if (!nav.items) return order.map((k) => builtIns[k]).filter(Boolean) as NavItem[];

    const list: NavItem[] = [];
    const listed = new Set<string>();
    nav.items.forEach((entry, i) => {
      if (entry.key === "custom") {
        if (!entry.hidden)
          list.push({
            key: `custom-${i}`,
            path: entry.url,
            label: entry.label,
            icon: CUSTOM_ICONS[entry.icon],
            primary: false,
            external: true,
          });
        return;
      }
      listed.add(entry.key);
      const item = builtIns[entry.key];
      if (!item || entry.hidden) return;
      // a renamed section shows the same name in the bottom bar
      list.push(entry.label ? { ...item, label: entry.label, tabLabel: entry.label } : item);
    });
    for (const k of order) {
      const item = builtIns[k];
      if (!listed.has(k) && item) list.push(item);
    }
    return list;
  }, [loc, nav.items, school.community?.enabled, school.gamification, school.partnership]);

  const first = `/${pathname.split("/")[1] ?? ""}`;
  const activeKey =
    items.find((x) => !x.external && x.path !== "/" && first === x.path)?.key ??
    (first === "/" || first === "/courses" || first === "/course" ? "learning" : undefined);

  return { items, activeKey };
}
